'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { OLAF } from 'HubStyleTokens/colors';
import { NavMarker } from 'react-rhumb';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import MobileFilterSection from '../../containers/TracksPage/MobileFilterSection';
import TracksTopFiltersContainer from '../../containers/TracksPage/TracksTopFiltersContainer';
import ApiError from '../Errors/ApiError';
import NoResultsError from '../Errors/NoResultsError';
import PaginatedUICardGrid from '../../containers/MyLearningPage/PaginatedUICardGrid';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { device } from '../../constants/StyledComponentConstants';
import { safeSmoothScrollTo } from '../../utils/utils';
import useMediaQuery from '../../hooks/useMediaQuery';
import { usePageNumber } from '../../hooks/searchParamHooks';
import ResultsStatusBar from '../ResultsStatusBar';
import WeekOfRestBanner from '../Banners/WeekOfRestBanner';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import useWootricSurvey from '../../hooks/useWootricSurvey';
/* Removes border at top of UIDashboard Page */

const CatalogPageWrapper = styled.div.withConfig({
  displayName: "CatalogPageLayout__CatalogPageWrapper",
  componentId: "e0i15g-0"
})(["[class^='UIAbstractPageTemplate'] > section + div{border-top:0;}"]);
const CardsContainer = styled.div.withConfig({
  displayName: "CatalogPageLayout__CardsContainer",
  componentId: "e0i15g-1"
})(["padding-top:15px;@media screen and ", "{padding-top:0;}"], device.mobile);

const CatalogPageLayout = ({
  pageUrlFragment,
  catalogCards,
  error,
  isRequestingCardsData
}) => {
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  const location = useLocation();
  const history = useHistory();
  useWootricSurvey();
  const pageNumber = usePageNumber(location);
  const [{
    startCard,
    endCard,
    cardsTotal
  }, setCardCountState] = useState({
    startCard: 0,
    endCard: 0,
    cardsTotal: 0
  });
  const onCalculateContents = useCallback(({
    endCard: end,
    cardsTotal: total,
    startCard: start
  }) => setCardCountState({
    endCard: end,
    startCard: start,
    cardsTotal: total
  }), []);
  useEffect(() => {
    if (!catalogCards.length && !isRequestingCardsData) {
      setCardCountState({
        endCard: 0,
        startCard: 0,
        cardsTotal: 0
      });
    }
  }, [catalogCards, isRequestingCardsData]);
  return /*#__PURE__*/_jsxs(CatalogPageWrapper, {
    children: [/*#__PURE__*/_jsx(ResultsStatusBar, {
      cardsTotal: cardsTotal,
      endCard: endCard,
      mobileFilterTypes: [FILTER_TYPES.AWARD_TYPE, FILTER_TYPES.LEVEL, FILTER_TYPES.DURATION, FILTER_TYPES.CONTENT_TYPE, FILTER_TYPES.LANGUAGE],
      startCard: startCard,
      sticky: true
    }), /*#__PURE__*/_jsx(WeekOfRestBanner, {}), /*#__PURE__*/_jsxs(UIDashboardPage, {
      contentAreaFlush: !!isMobile,
      headerComponent: null,
      mainSectionStyle: {
        backgroundColor: OLAF
      },
      navHeight: 311,
      children: [isMobile ? /*#__PURE__*/_jsx(MobileFilterSection, {}) : /*#__PURE__*/_jsx(TracksTopFiltersContainer, {}), isRequestingCardsData ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered",
        minHeight: 250,
        size: "medium"
      }) : error ? /*#__PURE__*/_jsx(ApiError, {
        className: "tracks-api-error",
        error: error,
        marker404: "CATALOG_COURSES_API_404",
        markerDefault: "CATALOG_COURSES_API_ERROR",
        resetLink: pageUrlFragment
      }) : !catalogCards.length ? /*#__PURE__*/_jsx(NavMarker, {
        name: "CATALOG_COURSES_LOADED",
        children: /*#__PURE__*/_jsx(NoResultsError, {
          resetLink: pageUrlFragment
        })
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: "CATALOG_COURSES_LOADED",
        children: /*#__PURE__*/_jsx(CardsContainer, {
          "data-test-id": "main-catalog-cards-container",
          children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
            columnSize: 250,
            limit: 12,
            onCalculateContents: onCalculateContents,
            onPageChange: page => {
              history.push(Object.assign({}, location, {
                pathname: `${pageUrlFragment}/page/${page}`
              }));
              safeSmoothScrollTo(window, 0, 0);
            },
            page: pageNumber,
            children: catalogCards
          })
        })
      })]
    })]
  });
};

CatalogPageLayout.propTypes = {
  catalogCards: PropTypes.arrayOf(PropTypes.node),
  error: PropTypes.object,
  isRequestingCardsData: PropTypes.bool,
  pageUrlFragment: PropTypes.string
};
export default CatalogPageLayout;